import ReactMarkdown, { defaultUrlTransform } from "react-markdown";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";
import { ComponentPropsWithoutRef } from "react";
import { cn } from "@/lib/utils";

type MarkdownProps = ComponentPropsWithoutRef<typeof ReactMarkdown>;

// URLs de host sem protocolo (ex.: "localhost:4200/..." ou "meu-host:8080/...") têm um ":" antes
// da primeira "/", então o sanitizador padrão do react-markdown as trata como um "protocolo"
// desconhecido e descarta o href (vira ""), fazendo o link apontar para a própria página atual.
// Quando o sanitizador descarta uma URL desse formato (host[:porta]/...), prefixamos "http://"
// para preservá-la em vez de deixá-la virar um link vazio.
const HOST_WITHOUT_PROTOCOL = /^[\w.-]+(:\d+)?\//;
const urlTransform: NonNullable<MarkdownProps["urlTransform"]> = (url) => {
  const transformed = defaultUrlTransform(url);
  if (transformed === "" && url !== "" && HOST_WITHOUT_PROTOCOL.test(url)) {
    return defaultUrlTransform(`http://${url}`);
  }
  return transformed;
};

const MarkdownRenderer = ({ content, className }: { content: string; className?: string }) => {
  const processedContent = content.replace(/\\n/g, "\n");

  const components: MarkdownProps["components"] = {
    // --- Títulos ---
    h1: ({ ...props }) => (
      <h1
        className="mb-4 mt-8 border-b border-border pb-2 text-2xl font-bold tracking-tight text-foreground sm:text-3xl"
        {...props}
      />
    ),
    h2: ({ ...props }) => (
      <h2
        className="mb-3 mt-6 text-xl font-semibold tracking-tight text-foreground sm:text-2xl"
        {...props}
      />
    ),
    h3: ({ ...props }) => (
      <h3
        className="mb-2 mt-5 text-lg font-semibold tracking-tight text-foreground sm:text-xl"
        {...props}
      />
    ),
    h4: ({ ...props }) => (
      <h4
        className="mb-2 mt-4 text-base font-semibold tracking-tight text-foreground sm:text-lg"
        {...props}
      />
    ),

    // --- Texto e Listas ---
    p: ({ ...props }) => (
      <p
        className="mb-4 text-sm leading-relaxed text-muted-foreground last:mb-0 dark:text-foreground sm:text-base"
        {...props}
      />
    ),
    ul: ({ ...props }) => (
      // marker:text-foreground/50 para bullets neutros adaptáveis ao tema
      <ul
        className="my-4 list-disc pl-6 text-sm text-muted-foreground marker:text-foreground/50 sm:text-base [&>li]:mt-1.5"
        {...props}
      />
    ),
    ol: ({ ...props }) => (
      <ol
        className="my-4 list-decimal pl-6 text-sm font-medium text-muted-foreground marker:text-foreground/50 sm:text-base [&>li]:mt-1.5"
        {...props}
      />
    ),
    li: ({ ...props }) => <li className="leading-relaxed text-muted-foreground" {...props} />,

    // --- Elementos de Ênfase e Citações ---
    strong: ({ ...props }) => <strong className="font-semibold text-foreground" {...props} />,
    em: ({ ...props }) => <em className="italic text-foreground/80" {...props} />,
    blockquote: ({ ...props }) => (
      <blockquote
        // Uso de cores semânticas do seu tema (border-primary, bg-muted)
        className="my-4 rounded-r-lg border-l-4 border-primary/50 bg-muted/30 py-3 pl-4 pr-4 italic text-muted-foreground [&>p]:mb-0"
        {...props}
      />
    ),

    // --- Separador ---
    hr: ({ ...props }) => <hr className="my-6 border-t-2 border-dashed border-border" {...props} />,

    // --- Links ---
    a: ({ ...props }) => (
      <a
        className="font-medium text-primary underline underline-offset-4 transition-colors hover:text-primary/80 dark:text-emerald-500"
        target="_blank"
        rel="noopener noreferrer"
        title="Link externo"
        {...props}
      />
    ),

    // --- Tabelas (Requer remark-gfm) ---
    table: ({ ...props }) => (
      <div className="table-scrollbar my-6 w-full overflow-x-auto rounded-lg border border-border">
        <table className="min-w-full border-collapse text-left text-sm" {...props} />
      </div>
    ),
    thead: ({ ...props }) => <thead className="bg-muted/50" {...props} />,
    tbody: ({ ...props }) => <tbody className="divide-y divide-border" {...props} />,

    th: ({ ...props }) => (
      <th
        className="whitespace-nowrap px-4 py-3 font-semibold text-foreground first:pl-6 last:pr-6"
        {...props}
      />
    ),
    td: ({ ...props }) => (
      <td
        className="min-w-[120px] whitespace-normal px-4 py-3 text-muted-foreground first:pl-6 last:pr-6"
        {...props}
      />
    ),

    tr: ({ ...props }) => <tr className="transition-colors hover:bg-muted/30" {...props} />,

    // --- Code Blocks ---
    code: ({ node, inline, className, children, ...props }: any) => {
      // Inline code (Ex: `texto`)
      if (inline) {
        return (
          <code
            // Estilo neutro para tags inline
            className="relative rounded bg-muted/60 px-[0.4rem] py-[0.2rem] font-mono text-[0.85em] font-semibold text-foreground"
            {...props}
          >
            {children}
          </code>
        );
      }

      // Block code (Ex: ```json ... ```)
      return (
        // Cores hardcoded escuras para blocos de código (padrão de mercado para legibilidade)
        // Usando sua classe custom-scrollbar
        <pre className="custom-scrollbar mb-4 mt-6 overflow-x-auto rounded-lg border border-border bg-zinc-950 p-4">
          <code className="font-mono text-sm leading-relaxed text-zinc-50" {...props}>
            {children}
          </code>
        </pre>
      );
    },
  };

  return (
    <div className={cn("markdown-renderer w-full max-w-full break-words", className)}>
      <ReactMarkdown
        components={components}
        remarkPlugins={[remarkBreaks, remarkGfm]}
        urlTransform={urlTransform}
      >
        {processedContent}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownRenderer;
